import { useDocChatAttachments } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocChatAttachmentsContext'
import { loadAttachments } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatAttachmentStore'
import {
  drainDocChatMessageQueue,
  getNextDocChatQueuedMessage,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatMessageQueue'
import type {
  SearchHistoryItem,
  SearchStateUpdate,
} from 'features/knowledgeDocumentViewer/types'
import { useEffect } from 'react'

export function useDocChatMessageQueue({
  searchState,
  siteId,
  onSearchStateChange,
}: {
  searchState: SearchHistoryItem['searchState']
  siteId: string
  onSearchStateChange: (update: SearchStateUpdate) => void
}): void {
  const { sentAttachmentsById, retainSentAttachments } = useDocChatAttachments()
  const message = getNextDocChatQueuedMessage(searchState, siteId)

  useEffect(() => {
    if (!message) return
    const missingIds = (message.attachments ?? [])
      .filter((attachment) => !sentAttachmentsById.has(attachment.id))
      .map((attachment) => attachment.id)
    if (!missingIds.length) {
      onSearchStateChange((current) =>
        getNextDocChatQueuedMessage(current, siteId) === message
          ? drainDocChatMessageQueue(current, siteId)
          : {}
      )
      return
    }

    let cancelled = false
    void loadAttachments(missingIds).then((restored) => {
      if (cancelled) return
      if (missingIds.every((id) => restored.some((image) => image.id === id))) {
        retainSentAttachments(restored)
        return
      }
      onSearchStateChange((current) => {
        const docChat = current.docChat
        if (
          !docChat ||
          getNextDocChatQueuedMessage(current, siteId) !== message
        ) {
          return {}
        }
        return {
          docChat: {
            ...docChat,
            queuedMessages: docChat.queuedMessages?.slice(1),
          },
        }
      })
    })
    return () => {
      cancelled = true
    }
  }, [
    message,
    onSearchStateChange,
    retainSentAttachments,
    sentAttachmentsById,
    siteId,
  ])
}
